/**
 * Filters Bar - Country, store, and sort filter controls
 */

import { useState } from 'react';
import { Globe, SlidersHorizontal, X, Share2, Check } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import StoreSelector from './StoreSelector';
import type { SortOption } from '../types';
import { SORT_OPTIONS } from '../utils/constants';
import { getCountryFlag, getCountryName } from '../utils/url';

interface Props {
  countries: string[];
  stores: Array<{
    id: string;
    name: string;
    city: string;
    state: string;
    region: 'midwest' | 'northeast' | 'southeast' | 'south' | 'west';
    country: 'US' | 'CA';
  }>;
  selectedCountry: string;
  selectedStoreId: string;
  selectedSort: SortOption;
  onCountryChange: (country: string) => void;
  onStoreChange: (storeId: string | null) => void;
  onSortChange: (sort: SortOption) => void;
  onShare?: () => void;
  isLoading?: boolean;
}

export default function FiltersBar({
  countries,
  stores,
  selectedCountry,
  selectedStoreId,
  selectedSort,
  onCountryChange,
  onStoreChange,
  onSortChange,
  onShare,
  isLoading,
}: Props) {
  const { t } = useLanguage();
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="bg-white border-b border-gray-100 py-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center gap-3">
          {/* Country Selector */}
          <div className="relative">
            <button
              onClick={() => setShowCountryDropdown(!showCountryDropdown)}
              className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-300 focus:border-gray-300 focus:outline-none focus:shadow-sm transition-all duration-200"
              disabled={isLoading}
            >
              <Globe className="w-4 h-4 text-gray-400" />
              {selectedCountry ? (
                <span className="flex items-center gap-1.5">
                  <span>{getCountryFlag(selectedCountry)}</span>
                  <span>{getCountryName(selectedCountry)}</span>
                </span>
              ) : (
                <span>{t('filters.selectCountry', 'Select Country')}</span>
              )}
            </button>

            {showCountryDropdown && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowCountryDropdown(false)}
                  onKeyDown={(e) => e.key === 'Escape' && setShowCountryDropdown(false)}
                  role="button"
                  tabIndex={-1}
                  aria-label={t('filters.closeDropdown', 'Close dropdown')}
                />
                <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50 py-2 max-h-64 overflow-y-auto">
                  {countries.map((country) => (
                    <button
                      key={country}
                      onClick={() => {
                        onCountryChange(country);
                        setShowCountryDropdown(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                        selectedCountry === country
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="text-lg">{getCountryFlag(country)}</span>
                        <span>{getCountryName(country)}</span>
                      </span>
                      {selectedCountry === country && (
                        <Check className="w-4 h-4 text-primary-600" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Store Selector */}
          <StoreSelector
            stores={stores}
            selectedStoreId={selectedStoreId}
            onSelect={(storeId) => onStoreChange(storeId)}
            country={selectedCountry}
          />

          {/* Spacer */}
          <div className="flex-1" />

          {/* Sort Selector */}
          <div className="relative">
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium text-gray-700 hover:border-gray-300 focus:border-gray-300 focus:outline-none focus:shadow-sm transition-all duration-200"
            >
              <SlidersHorizontal className="w-4 h-4 text-gray-400" />
              <span>
                {SORT_OPTIONS.find((opt) => opt.value === selectedSort)?.label ||
                  t('filters.sort', 'Sort')}
              </span>
            </button>

            {showSortDropdown && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowSortDropdown(false)}
                  onKeyDown={(e) => e.key === 'Escape' && setShowSortDropdown(false)}
                  role="button"
                  tabIndex={-1}
                  aria-label={t('filters.closeDropdown', 'Close dropdown')}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 z-50 py-2">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        onSortChange(option.value);
                        setShowSortDropdown(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                        selectedSort === option.value
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-700'
                      }`}
                    >
                      <span>{option.label}</span>
                      {selectedSort === option.value && (
                        <Check className="w-4 h-4 text-primary-600" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Share Button */}
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-3 py-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            title={t('filters.share', 'Share filters')}
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Share2 className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Active Filters */}
        {(selectedStoreId || selectedSort !== 'newest') && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <span className="text-xs text-gray-500">{t('filters.activeFilters', 'Active:')}</span>

            {selectedStoreId && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                {stores.find((s) => s.id === selectedStoreId)?.name || selectedStoreId}
                <button
                  onClick={() => onStoreChange(null)}
                  className="hover:text-primary-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            {selectedSort !== 'newest' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                {SORT_OPTIONS.find((opt) => opt.value === selectedSort)?.label}
                <button
                  onClick={() => onSortChange('newest')}
                  className="hover:text-gray-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
